
import React, { useState } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from '../../components/ui/select';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger 
} from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { format } from 'date-fns';

interface Department {
  id: string;
  name: string;
  subject: string;
  classType: string;
  startTime: string;
  endTime: string;
  days: string[];
}

interface TeacherDepartmentFieldsProps {
  formData: any;
  handleDepartmentChange: (index: number, field: string, value: any) => void;
  addDepartment: () => void;
}

const TeacherDepartmentFields = ({ 
  formData, 
  handleDepartmentChange, 
  addDepartment 
}: TeacherDepartmentFieldsProps) => {
  const departments = formData.departments || [];
  
  const removeDepartment = (index: number) => {
    const newDepartments = [...departments];
    newDepartments.splice(index, 1);
    handleDepartmentChange(0, 'departments', newDepartments);
  };

  return (
    <div className="space-y-6">
      {departments.length === 0 ? (
        <div className="text-center p-8 border border-dashed rounded-lg">
          <p className="text-muted-foreground">No departments added yet</p>
        </div>
      ) : (
        departments.map((dept: any, index: number) => (
          <div key={dept.id} className="p-4 border rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">Department {index + 1}</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => removeDepartment(index)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`dept-name-${index}`}>Department Name</Label>
                <Input
                  id={`dept-name-${index}`}
                  value={dept.name || ''}
                  onChange={(e) => handleDepartmentChange(index, 'name', e.target.value)}
                  placeholder="e.g. Mathematics"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`subject-${index}`}>Subject</Label>
                <Input
                  id={`subject-${index}`}
                  value={dept.subject || ''}
                  onChange={(e) => handleDepartmentChange(index, 'subject', e.target.value)}
                  placeholder="e.g. Algebra"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`class-type-${index}`}>Class Type</Label>
                <Select 
                  value={dept.classType || ''} 
                  onValueChange={(value) => handleDepartmentChange(index, 'classType', value)}
                >
                  <SelectTrigger id={`class-type-${index}`}>
                    <SelectValue placeholder="Select class type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="elective">Elective</SelectItem>
                    <SelectItem value="lab">Laboratory</SelectItem>
                    <SelectItem value="seminar">Seminar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor={`start-time-${index}`}>Start Time</Label>
                  <Input
                    id={`start-time-${index}`}
                    type="time"
                    value={dept.startTime || ''}
                    onChange={(e) => {
                      // Convert 24-hour time to internal representation
                      handleDepartmentChange(index, 'startTime', e.target.value);
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`end-time-${index}`}>End Time</Label>
                  <Input
                    id={`end-time-${index}`}
                    type="time"
                    value={dept.endTime || ''}
                    onChange={(e) => {
                      // Convert 24-hour time to internal representation
                      handleDepartmentChange(index, 'endTime', e.target.value);
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-2 col-span-1 md:col-span-2">
                <Label>Days</Label>
                <div className="flex flex-wrap gap-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                    const isSelected = dept.days?.includes(day);
                    return (
                      <Button
                        key={day}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          const currentDays = dept.days || [];
                          const newDays = isSelected
                            ? currentDays.filter((d: string) => d !== day)
                            : [...currentDays, day];
                          handleDepartmentChange(index, 'days', newDays);
                        }}
                      >
                        {day.substring(0, 3)}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
      
      <Button 
        type="button"
        variant="outline" 
        className="w-full"
        onClick={addDepartment}
      >
        <PlusCircle className="h-4 w-4 mr-2" />
        Add Department
      </Button>
    </div>
  );
};

export default TeacherDepartmentFields;
